import React, { useState } from 'react';
import axios from 'axios';
import logo from '../assets/5star-logo.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

// Step constants
const STEP_LOGIN = 'login';
const STEP_TOTP = 'totp';
const STEP_SETUP_SCAN = 'setup-scan';
const STEP_SETUP_VERIFY = 'setup-verify';

export default function Login({ onLogin }) {
  const [step, setStep] = useState(STEP_LOGIN);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, { username, password });
      setUserName(res.data.name);
      if (res.data.totpEnabled) {
        setStep(STEP_TOTP);
      } else if (res.data.token) {
        // No TOTP required — already logged in
        localStorage.setItem('auth_token', res.data.token);
        localStorage.setItem('auth_name', res.data.name);
        onLogin(res.data.token, res.data.name);
      } else {
        // First time — need to set up TOTP
        const setupRes = await axios.post(`${API_URL}/api/auth/setup-totp`, { username, password });
        setQrCode(setupRes.data.qrCode);
        setTotpSecret(setupRes.data.secret);
        setStep(STEP_SETUP_SCAN);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/verify-totp`, { username, token: totpCode });
      localStorage.setItem('auth_token', res.data.token);
      localStorage.setItem('auth_name', res.data.name);
      onLogin(res.data.token, res.data.name);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/verify-setup`, { username, token: totpCode });
      localStorage.setItem('auth_token', res.data.token);
      localStorage.setItem('auth_name', res.data.name);
      onLogin(res.data.token, res.data.name);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpInput = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setTotpCode(val);
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoWrap}>
          <img src={logo} alt="5 Star Truck Sales" style={styles.logo} />
        </div>

        {/* Title */}
        <div style={styles.titleBlock}>
          <h1 style={styles.title}>Document Portal</h1>
          <div style={styles.titleBar} />
        </div>

        {/* STEP 1: Username + Password */}
        {step === STEP_LOGIN && (
          <form onSubmit={handleLogin} style={styles.form}>
            <p style={styles.subtitle}>Sign in to continue</p>

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Username</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                style={styles.input}
                autoFocus
                autoComplete="username"
                placeholder="Enter your username"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={styles.input}
                autoComplete="current-password"
                placeholder="Enter your password"
              />
            </div>

            <button type="submit" disabled={loading || !username || !password} style={styles.btn}>
              {loading ? 'Verifying...' : 'Continue →'}
            </button>
          </form>
        )}

        {/* STEP 2: Enter TOTP Code */}
        {step === STEP_TOTP && (
          <form onSubmit={handleVerifyTOTP} style={styles.form}>
            <p style={styles.subtitle}>Welcome back, <strong>{userName}</strong></p>
            <p style={styles.instructions}>
              Open your authenticator app and enter the 6-digit code for <strong>5 Star Truck Sales</strong>.
            </p>

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Authentication Code</label>
              <input
                type="text"
                inputMode="numeric"
                value={totpCode}
                onChange={handleTotpInput}
                style={{...styles.input, ...styles.codeInput}}
                autoFocus
                placeholder="000000"
                maxLength={6}
              />
            </div>

            <button type="submit" disabled={loading || totpCode.length !== 6} style={styles.btn}>
              {loading ? 'Verifying...' : 'Sign In'}
            </button>

            <button type="button" onClick={() => { setStep(STEP_LOGIN); setError(''); }} style={styles.backBtn}>
              ← Back
            </button>
          </form>
        )}

        {/* STEP 3: First-time TOTP Setup - Scan QR */}
        {step === STEP_SETUP_SCAN && (
          <div style={styles.form}>
            <p style={styles.subtitle}>One-time setup required</p>
            <p style={styles.instructions}>
              Scan this QR code with <strong>Google Authenticator</strong>, <strong>Microsoft Authenticator</strong>, or <strong>Authy</strong>.
            </p>

            {qrCode && (
              <div style={styles.qrWrap}>
                <img src={qrCode} alt="TOTP QR Code" style={styles.qrCode} />
              </div>
            )}

            <details style={styles.manualDetails}>
              <summary style={styles.manualSummary}>Can't scan? Enter manually</summary>
              <code style={styles.secretCode}>{totpSecret}</code>
            </details>

            <button
              type="button"
              onClick={() => { setStep(STEP_SETUP_VERIFY); setTotpCode(''); }}
              style={styles.btn}
            >
              I've scanned it →
            </button>
          </div>
        )}

        {/* STEP 4: First-time TOTP Setup - Verify */}
        {step === STEP_SETUP_VERIFY && (
          <form onSubmit={handleVerifySetup} style={styles.form}>
            <p style={styles.subtitle}>Confirm your setup</p>
            <p style={styles.instructions}>
              Enter the 6-digit code from your authenticator app to confirm setup.
            </p>

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                value={totpCode}
                onChange={handleTotpInput}
                style={{...styles.input, ...styles.codeInput}}
                autoFocus
                placeholder="000000"
                maxLength={6}
              />
            </div>

            <button type="submit" disabled={loading || totpCode.length !== 6} style={styles.btn}>
              {loading ? 'Confirming...' : 'Confirm & Sign In'}
            </button>

            <button type="button" onClick={() => { setStep(STEP_SETUP_SCAN); setError(''); }} style={styles.backBtn}>
              ← Back to QR code
            </button>
          </form>
        )}

        <div style={styles.footer}>
          5 Star Truck Sales © {new Date().getFullYear()} &nbsp;·&nbsp; Byron Center, MI
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0f0f0f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  card: {
    background: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '16px',
    padding: '48px 44px 36px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
  },
  logoWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logo: {
    width: '140px',
    height: 'auto',
    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.5))',
  },
  titleBlock: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 10px 0',
    letterSpacing: '0.5px',
  },
  titleBar: {
    width: '48px',
    height: '3px',
    background: '#cc1111',
    margin: '0 auto',
    borderRadius: '2px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
  subtitle: {
    color: '#aaa',
    fontSize: '14px',
    margin: '0 0 20px 0',
    textAlign: 'center',
  },
  instructions: {
    color: '#888',
    fontSize: '13px',
    lineHeight: '1.6',
    margin: '0 0 20px 0',
    textAlign: 'center',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  label: {
    color: '#888',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },
  input: {
    background: '#111',
    border: '1px solid #333',
    borderRadius: '8px',
    padding: '12px 14px',
    color: '#fff',
    fontSize: '15px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  codeInput: {
    fontSize: '28px',
    letterSpacing: '12px',
    textAlign: 'center',
    fontWeight: '700',
    padding: '14px',
  },
  btn: {
    background: '#cc1111',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'background 0.2s',
    letterSpacing: '0.3px',
  },
  backBtn: {
    background: 'transparent',
    color: '#666',
    border: 'none',
    padding: '10px',
    fontSize: '13px',
    cursor: 'pointer',
    marginTop: '4px',
    textAlign: 'center',
  },
  errorBox: {
    background: 'rgba(204,17,17,0.15)',
    border: '1px solid rgba(204,17,17,0.4)',
    borderRadius: '8px',
    color: '#ff6b6b',
    padding: '10px 14px',
    fontSize: '13px',
    marginBottom: '16px',
    textAlign: 'center',
  },
  qrWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
    background: '#fff',
    borderRadius: '12px',
    padding: '16px',
  },
  qrCode: {
    width: '200px',
    height: '200px',
  },
  manualDetails: {
    marginBottom: '20px',
  },
  manualSummary: {
    color: '#666',
    fontSize: '12px',
    cursor: 'pointer',
    textAlign: 'center',
    marginBottom: '8px',
  },
  secretCode: {
    display: 'block',
    background: '#111',
    border: '1px solid #333',
    borderRadius: '6px',
    padding: '10px',
    color: '#aaa',
    fontSize: '11px',
    wordBreak: 'break-all',
    textAlign: 'center',
    marginTop: '8px',
  },
  footer: {
    marginTop: '32px',
    color: '#444',
    fontSize: '11px',
    textAlign: 'center',
  }
};
